"use client"

import type { ChangeEvent } from "react"
import Image from "next/image"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import {
  DEFAULT_PHONE_COUNTRY_CODE,
  PHONE_COUNTRIES,
  type PhoneCountryCode,
  splitPhoneNumber,
} from "@/lib/phone"

type PhoneNumberInputProps = {
  id?: string
  name?: string
  value: string
  onChange: (value: string) => void
  countryCode: PhoneCountryCode
  onCountryCodeChange: (value: PhoneCountryCode) => void
  placeholder?: string
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  autoFocus?: boolean
  autoComplete?: string
  className?: string
  inputClassName?: string
  selectClassName?: string
  getCountryLabel?: (countryCode: PhoneCountryCode) => string
}

export function PhoneNumberInput({
  id,
  name,
  value,
  onChange,
  countryCode,
  onCountryCodeChange,
  placeholder,
  disabled,
  readOnly,
  required,
  autoFocus,
  autoComplete = "tel",
  className,
  inputClassName,
  selectClassName,
  getCountryLabel,
}: PhoneNumberInputProps) {
  // Ensure we always have a valid country code that exists in PHONE_COUNTRIES
  const validCountryCode: PhoneCountryCode =
    PHONE_COUNTRIES.some((c) => c.code === countryCode)
      ? countryCode
      : DEFAULT_PHONE_COUNTRY_CODE

  const selectedCountry =
    PHONE_COUNTRIES.find((country) => country.code === validCountryCode) ??
    PHONE_COUNTRIES[0]

  // Ensure selectedCountry is always defined
  if (!selectedCountry) {
    console.error('No country found for code:', validCountryCode)
  }

  const getCountryName = (code: PhoneCountryCode): string => {
    if (getCountryLabel) {
      return getCountryLabel(code)
    }
    const country = PHONE_COUNTRIES.find((c) => c.code === code)
    return country?.label ?? code
  }

  const getDefaultPlaceholder = (): string => {
    switch (countryCode) {
      case "+55":
        return "11 55555 4444"
      case "+1":
        return "111 222 4444"
      default:
        return "Enter your phone number"
    }
  }

  const displayPlaceholder = placeholder ?? getDefaultPlaceholder()

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value
    const parsed = splitPhoneNumber(nextValue, countryCode)

    if (parsed.detected && parsed.countryCode !== countryCode) {
      onCountryCodeChange(parsed.countryCode)
    }

    onChange(parsed.localNumber)
  }

  const isSelectDisabled = Boolean(disabled || readOnly)

  return (
    <div className={cn("flex gap-2", className)}>
      <Select
        key={validCountryCode}
        value={validCountryCode}
        onValueChange={(value) => onCountryCodeChange(value as PhoneCountryCode)}
        disabled={isSelectDisabled}
      >
        <SelectTrigger className={cn("w-[100px] shrink-0", selectClassName)}>
          <SelectValue>
            <span className="flex items-center gap-2">
              <Image
                src={selectedCountry.flagSrc}
                alt={selectedCountry.flagAlt}
                width={18}
                height={12}
                className="rounded-sm"
              />
              <span className="text-sm font-medium">{selectedCountry.code}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {PHONE_COUNTRIES.map((country) => (
            <SelectItem key={country.code} value={country.code}>
              <span className="flex items-center gap-2">
                <Image
                  src={country.flagSrc}
                  alt={country.flagAlt}
                  width={18}
                  height={12}
                  className="rounded-sm"
                />
                <span className="text-sm font-medium">{country.code}</span>
                <span className="text-xs text-muted-foreground">
                  {getCountryName(country.code)}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        id={id}
        name={name}
        type="tel"
        autoComplete={autoComplete}
        placeholder={displayPlaceholder}
        value={value}
        onChange={handleInputChange}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        autoFocus={autoFocus}
        className={cn("flex-1", inputClassName)}
      />
    </div>
  )
}
